/**
 * Sales Analytics Tracker (GDPR-compliant, anonymized)
 *
 * TWO LAYERS:
 *
 * Ebene 2 – Local (per user):
 *   World/{userId}/salesAnalytics/{hashedId}
 *   One doc per anonymous lead. User's personal decision brain.
 *
 * Ebene 1 – Global (cross-account, internal):
 *   globalSalesPatterns/{autoId}
 *   Pure pattern data. No userId, no contactId, no hash.
 *   Published when a journey reaches a terminal outcome (meeting/deal).
 *   This trains the platform-wide intelligence engine.
 *
 * Zero PII stored in either layer.
 * Every public function is fire-and-forget.
 */

import { doc, getDoc, setDoc, deleteDoc, collection, addDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firestore, functions } from '../firebaseConfig';
import { REPLY_SUBTYPES as _REPLY_SUBTYPES, VALID_INDUSTRIES as _INDUSTRIES_ARR } from '../shared/enums';
import { minimalSignalsSnapshot } from './leadSignalEngine';

// ──────────────────────────────────────────────
// Anonymization (SHA-256 via Web Crypto API)
// ──────────────────────────────────────────────

const hashCache = new Map();
const dsarWritten = new Set();

async function hashContactId(userId, contactId) {
  const key = `${userId}:${contactId}`;
  if (hashCache.has(key)) return hashCache.get(key);

  const encoder = new TextEncoder();
  const data = encoder.encode(`sa_v1:${key}`);
  const buf = await crypto.subtle.digest('SHA-256', data);
  const hex = Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  const id = `a_${hex.substring(0, 16)}`;
  hashCache.set(key, id);

  // DSAR lookup: fire-and-forget write of contactId → anonId mapping
  // Idempotent (merge: true), written once per session per contact.
  // NOTE: this is a detached (un-awaited) promise, so a synchronous try/catch
  // cannot catch its rejection — we MUST attach .catch(). dsarLookup is
  // owner-only, so for team members this write is denied ("Missing or
  // insufficient permissions"); swallowing it prevents an uncaught runtime
  // error overlay while owner writes still succeed.
  if (!dsarWritten.has(key)) {
    dsarWritten.add(key);
    try {
      const lookupRef = doc(firestore, `World/${userId}/dsarLookup/${contactId}`);
      setDoc(lookupRef, { anonId: id, updatedAt: Date.now() }, { merge: true })
        .catch(() => { /* non-critical: owner-only doc, denied for team members */ });
    } catch (_) { /* non-critical */ }
  }

  return id;
}

// ──────────────────────────────────────────────
// Touchpoint → Channel mapping
// ──────────────────────────────────────────────

const CHANNEL_MAP = {
  email_sent: 'email', cold_email: 'email', follow_up_email: 'email',
  nurture_email: 'email', reminder_email: 'email', case_study_email: 'email',
  testimonial_email: 'email', newsletter_sent: 'email', newsletter: 'email',
  email_opened: 'email', email_replied: 'email', sms_sent: 'email',

  linkedin_connect: 'linkedin', linkedin_connected: 'linkedin',
  linkedin_message: 'linkedin', linkedin_voice: 'linkedin',
  linkedin_like: 'linkedin', linkedin_comment: 'linkedin',
  linkedin_share: 'linkedin', linkedin_endorsed: 'linkedin',
  linkedin_recommendation: 'linkedin',

  instagram_follow: 'instagram', instagram_followed_back: 'instagram',
  instagram_dm: 'instagram', instagram_voice: 'instagram',
  instagram_like: 'instagram', instagram_comment: 'instagram',
  instagram_story_reply: 'instagram', instagram_story_mention: 'instagram',
  instagram_reel_comment: 'instagram', instagram_collab: 'instagram',

  whatsapp_message: 'whatsapp', whatsapp_voice: 'whatsapp',
  whatsapp_call: 'whatsapp', whatsapp_video: 'whatsapp',
  whatsapp_group: 'whatsapp', whatsapp_broadcast: 'whatsapp',

  phone_call: 'phone', discovery_call: 'phone', follow_up_call: 'phone',

  zoom_call_scheduled: 'meeting', meet_in_person: 'meeting',
  sales_meeting: 'meeting', online_meeting: 'meeting',
  video_call: 'meeting', lunch_meeting: 'meeting',
  networking_event: 'meeting',

  twitter_follow: 'social', twitter_dm: 'social', twitter_reply: 'social',
  twitter_retweet: 'social', facebook_connect: 'social',
  facebook_message: 'social', tiktok_follow: 'social',
  tiktok_comment: 'social', youtube_subscribe: 'social',
  youtube_comment: 'social',

  loom_video: 'content', video_message: 'content', voice_message: 'content',
  video_testimonial: 'content', webinar: 'content', webinar_attended: 'content',
  webinar_hosted: 'content', workshop_invite: 'content', podcast_guest: 'content',
  lead_magnet: 'content', ebook_sent: 'content', whitepaper_sent: 'content',
  case_study: 'content', case_study_sent: 'content', free_trial: 'content',
  discount_offer: 'content', workshop: 'content',

  gift_sent: 'gift', handwritten_note: 'gift', birthday_wish: 'gift',
  thank_you_card: 'gift', referral_bonus: 'gift', discount_offered: 'gift',
};

function mapToChannel(touchpointType) {
  return CHANNEL_MAP[touchpointType] || 'other';
}

// ──────────────────────────────────────────────
// Enthusiasm score → reply type classification
// ──────────────────────────────────────────────

export function classifyReplyType(enthusiasmScore) {
  if (enthusiasmScore == null) return 'neutral';
  const s = Number(enthusiasmScore);
  if (s <= 3) return 'objection';
  if (s <= 6) return 'neutral';
  return 'positive';
}

export const REPLY_SUBTYPES = _REPLY_SUBTYPES;

export function validateReplySubtype(v) {
  return v && REPLY_SUBTYPES.includes(v) ? v : 'neutral';
}

/**
 * Builds a causal segment key from leadSignals.
 * Format: offerType:b2b_b2c:tag1+tag2:sizeProxy
 *
 * - b2b_b2c separates fundamentally different market dynamics
 * - Tags: top 2 sorted alphabetically; falls back to industry slug when no tags exist
 *   (avoids a giant heterogeneous "_none" bucket)
 * - Backward-compat: old 3-part keys remain readable, new 4-part keys coexist
 */
export function buildSegmentKey(signals) {
  if (!signals) return 'unknown:unknown:_none:unknown';

  const offer = signals.offerType || 'unknown';
  const b2b = signals.b2b_b2c || 'unknown';
  const size = signals.sizeProxy || 'unknown';

  const tags = Array.isArray(signals.tags) ? signals.tags.filter(Boolean).sort() : [];
  let tagPart;
  if (tags.length >= 2) {
    tagPart = `${tags[0]}+${tags[1]}`;
  } else if (tags.length === 1) {
    tagPart = tags[0];
  } else {
    const ind = (signals.industry || '').toLowerCase().replace(/[\s&]+/g, '_');
    tagPart = ind && ind !== 'unknown' && ind !== 'other' ? ind : '_none';
  }

  return `${offer}:${b2b}:${tagPart}:${size}`;
}

// ──────────────────────────────────────────────
// Core: log a touchpoint / channel interaction
// ──────────────────────────────────────────────

/**
 * Records a single interaction in the anonymized analytics profile.
 *
 * @param {string} userId       – World owner ID
 * @param {string} contactId    – Firestore contact doc ID (will be hashed)
 * @param {object} event
 * @param {string} event.touchpointType – e.g. "cold_email", "linkedin_dm"
 * @param {string} [event.stage]        – current follow-up stage
 * @param {string} [event.pacing]       – formal / neutral / casual
 * @param {string} [event.industry]     – lead industry category
 * @param {string} [event.size]         – lead size category
 */
export const logSalesAnalytics = async (userId, contactId, event = {}) => {
  try {
    if (!userId || !contactId) return;

    const anonId = await hashContactId(userId, contactId);
    const docRef = doc(firestore, `World/${userId}/salesAnalytics/${anonId}`);
    const snap = await getDoc(docRef);
    const cur = snap.exists() ? snap.data() : null;

    // Auto-enrich from leadSignals if no industry/size in event
    let eventIndustry = event.industry;
    let eventSize = event.size;
    if (!eventIndustry || !eventSize) {
      try {
        const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);
        const contactSnap = await getDoc(contactRef);
        const signals = contactSnap.exists() ? contactSnap.data()?.leadSignals : null;
        if (signals) {
          if (!eventIndustry && signals.industry && signals.industry !== 'unknown' && (signals.industryConf || 0) >= 0.65) {
            eventIndustry = signals.industry;
          }
          if (!eventSize && signals.sizeProxy && signals.sizeProxy !== 'unknown' && (signals.sizeProxyConf || 0) >= 0.65) {
            eventSize = mapSizeProxyToAnalytics(signals.sizeProxy);
          }
        }
      } catch (_) { /* non-critical */ }
    }

    const now = Date.now();
    const channel = mapToChannel(event.touchpointType);
    const detail = event.touchpointType || 'unknown';
    const firstTouch = cur?.firstTouch || now;
    const daysSinceFirst = ((now - firstTouch) / 86400000).toFixed(1);

    const byChannel = { ...(cur?.byChannel || {}) };
    byChannel[channel] = (byChannel[channel] || 0) + 1;

    await setDoc(docRef, {
      channelSeq: cur?.channelSeq ? `${cur.channelSeq}>${channel}` : channel,
      detailSeq: cur?.detailSeq ? `${cur.detailSeq}>${detail}` : detail,
      daySeq: cur?.daySeq ? `${cur.daySeq}>${daysSinceFirst}` : '0.0',
      totalTouches: (cur?.totalTouches || 0) + 1,
      byChannel,
      industry: validateIndustry(eventIndustry) || cur?.industry || null,
      size: validateSize(eventSize) || cur?.size || null,
      stage: event.stage || cur?.stage || null,
      pacing: event.pacing || cur?.pacing || null,
      meeting: cur?.meeting || false,
      deal: cur?.deal || false,
      dealValue: cur?.dealValue || null,
      replyCount: cur?.replyCount || 0,
      avgDaysToReply: cur?.avgDaysToReply || null,
      replyTypes: cur?.replyTypes || {},
      bestScore: cur?.bestScore || null,
      firstTouch,
      lastTouch: now,
      updatedAt: now,
    });
  } catch (e) {
    console.warn('[SalesAnalytics] touch error (non-critical):', e.message);
  }
};

// ──────────────────────────────────────────────
// Record a reply event (called from backend helper via frontend or backend)
// ──────────────────────────────────────────────

/**
 * Updates the analytics profile with reply data.
 *
 * @param {string} userId
 * @param {string} contactId
 * @param {object} replyData
 * @param {number} replyData.daysToReply      – days between last email and reply
 * @param {number} [replyData.enthusiasmScore] – 1-10
 * @param {string} [replyData.replyType]       – positive / objection / neutral (auto-classified if score given)
 * @param {string} [replyData.replySubtype]    – one of REPLY_SUBTYPES (from LLM classification)
 */
export const logReplyAnalytics = async (userId, contactId, replyData = {}) => {
  try {
    if (!userId || !contactId) return;

    const anonId = await hashContactId(userId, contactId);
    const docRef = doc(firestore, `World/${userId}/salesAnalytics/${anonId}`);
    const snap = await getDoc(docRef);
    const cur = snap.exists() ? snap.data() : null;
    if (!cur) return;

    const rType = validateReplyType(replyData.replyType || classifyReplyType(replyData.enthusiasmScore));
    const subtype = validateReplySubtype(replyData.replySubtype);
    const dtr = replyData.daysToReply;
    const score = replyData.enthusiasmScore ? Number(replyData.enthusiasmScore) : null;

    const newCount = (cur.replyCount || 0) + 1;
    const replyTypes = { ...(cur.replyTypes || {}) };
    replyTypes[rType] = (replyTypes[rType] || 0) + 1;

    const replySubtypes = { ...(cur.replySubtypes || {}) };
    replySubtypes[subtype] = (replySubtypes[subtype] || 0) + 1;

    let avgDtr = cur.avgDaysToReply;
    if (dtr != null) {
      avgDtr = cur.avgDaysToReply != null
        ? ((cur.avgDaysToReply * (newCount - 1)) + dtr) / newCount
        : dtr;
      avgDtr = Math.round(avgDtr * 10) / 10;
    }

    const bestScore = score != null
      ? Math.max(score, cur.bestScore || 0)
      : cur.bestScore;

    const merged = {
      ...cur,
      replyCount: newCount,
      replyTypes,
      replySubtypes,
      lastReplySubtype: subtype,
      avgDaysToReply: avgDtr,
      bestScore,
      updatedAt: Date.now(),
    };

    try {
      const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);
      const contactSnap = await getDoc(contactRef);
      if (contactSnap.exists()) {
        const signals = contactSnap.data()?.leadSignals;
        if (signals) {
          merged.lastReplySignals = minimalSignalsSnapshot(signals);
          merged.lastReplySegmentKey = buildSegmentKey(signals);
        }
      }
    } catch (_) {}

    await setDoc(docRef, merged);

    const NEGATIVE_SUBTYPES = new Set(['hostile', 'spam_accusation', 'no_need', 'wrong_person']);
    if (NEGATIVE_SUBTYPES.has(subtype)) {
      recordSignalOutcome(userId, contactId, merged, false, merged.lastReplySignals || null).catch(() => {});
    }
  } catch (e) {
    console.warn('[SalesAnalytics] reply error (non-critical):', e.message);
  }
};

// ──────────────────────────────────────────────
// Record outcome changes (meeting / deal)
// ──────────────────────────────────────────────

/**
 * @param {string} userId
 * @param {string} contactId
 * @param {object} outcome
 * @param {boolean} [outcome.meeting]  – meeting booked
 * @param {boolean} [outcome.deal]     – deal closed
 * @param {number}  [outcome.dealValue]
 */
export const logOutcomeAnalytics = async (userId, contactId, outcome = {}) => {
  try {
    if (!userId || !contactId) return;

    const anonId = await hashContactId(userId, contactId);
    const docRef = doc(firestore, `World/${userId}/salesAnalytics/${anonId}`);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return;

    const cur = snap.data();
    const updates = { updatedAt: Date.now() };

    if (outcome.meeting != null) updates.meeting = outcome.meeting;
    if (outcome.deal != null) updates.deal = outcome.deal;
    if (outcome.dealValue != null) updates.dealValue = outcome.dealValue;

    const merged = { ...cur, ...updates };
    await setDoc(docRef, merged);

    // Auto-publish to Ebene 1 (Global) when a meaningful outcome is reached
    const isNewOutcome =
      (outcome.meeting && !cur.meeting) ||
      (outcome.deal && !cur.deal);
    if (isNewOutcome) {
      publishToGlobal(merged);
      // Feedback loop: record which signal combinations led to conversion
      recordSignalOutcome(userId, contactId, merged, true).catch(() => {});
    }
  } catch (e) {
    console.warn('[SalesAnalytics] outcome error (non-critical):', e.message);
  }
};

// ──────────────────────────────────────────────
// FEEDBACK LOOP: Signal → Outcome Learning
// ──────────────────────────────────────────────

/**
 * Records which signal combination led to a positive or negative outcome.
 * Stored in World/{userId}/signalPerformance/{segmentKey}
 *
 * segmentKey = offerType:b2b_b2c:tags:sizeProxy (causal segment)
 */
const recordSignalOutcome = async (userId, contactId, analyticsProfile, isPositive, prefetchedSignals) => {
  try {
    let signals = prefetchedSignals;
    if (!signals) {
      const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);
      const contactSnap = await getDoc(contactRef);
      if (!contactSnap.exists()) return;
      signals = contactSnap.data()?.leadSignals;
    }
    if (!signals) return;

    const segKey = buildSegmentKey(signals);

    const fullSignalSnapshot = minimalSignalsSnapshot(signals);

    const perfRef = doc(firestore, `World/${userId}/signalPerformance/${segKey}`);
    const perfSnap = await getDoc(perfRef);
    const cur = perfSnap.exists() ? perfSnap.data() : {
      segment: {
        offerType: signals.offerType || 'unknown',
        b2b_b2c: signals.b2b_b2c || 'unknown',
        industry: signals.industry || 'unknown',
        tags: (signals.tags || []).sort().slice(0, 2),
        sizeProxy: signals.sizeProxy || 'unknown',
      },
      totalContacts: 0,
      meetings: 0,
      deals: 0,
      negativeOutcomes: 0,
      totalTouches: 0,
      avgTouchesToConvert: null,
      replySubtypes: {},
      createdAt: Date.now(),
    };

    cur.totalContacts = (cur.totalContacts || 0) + 1;
    if (isPositive) {
      if (analyticsProfile.meeting) cur.meetings = (cur.meetings || 0) + 1;
      if (analyticsProfile.deal) cur.deals = (cur.deals || 0) + 1;
    } else {
      cur.negativeOutcomes = (cur.negativeOutcomes || 0) + 1;
    }

    const touches = analyticsProfile.totalTouches || 0;
    cur.totalTouches = (cur.totalTouches || 0) + touches;
    if (isPositive && touches > 0) {
      const prevAvg = cur.avgTouchesToConvert;
      const prevCount = (cur.meetings || 0) + (cur.deals || 0) - 1;
      if (prevAvg != null && prevCount > 0) {
        cur.avgTouchesToConvert = parseFloat(((prevAvg * prevCount + touches) / (prevCount + 1)).toFixed(1));
      } else {
        cur.avgTouchesToConvert = touches;
      }
    }

    if (isPositive && analyticsProfile.channelSeq) {
      if (!cur.winningSequences) cur.winningSequences = {};
      const seq = analyticsProfile.channelSeq;
      cur.winningSequences[seq] = (cur.winningSequences[seq] || 0) + 1;
    }

    if (analyticsProfile.replySubtypes) {
      if (!cur.replySubtypes) cur.replySubtypes = {};
      for (const [st, count] of Object.entries(analyticsProfile.replySubtypes)) {
        cur.replySubtypes[st] = (cur.replySubtypes[st] || 0) + count;
      }
    }

    cur.conversionRate = cur.totalContacts > 0
      ? parseFloat(((cur.meetings + cur.deals) / cur.totalContacts * 100).toFixed(1))
      : 0;

    if (!cur.signalSnapshots) cur.signalSnapshots = [];
    cur.signalSnapshots.push({
      ...fullSignalSnapshot,
      outcome: isPositive ? 'positive' : 'negative',
      ts: Date.now(),
    });
    if (cur.signalSnapshots.length > 50) {
      cur.signalSnapshots = cur.signalSnapshots.slice(-50);
    }

    cur.updatedAt = Date.now();

    await setDoc(perfRef, cur);
  } catch (e) {
    console.warn('[SignalFeedback] recordSignalOutcome error:', e.message);
  }
};

export const getSegmentPerformance = async (userId, signals) => {
  try {
    if (!userId || !signals) return null;

    const segKey = buildSegmentKey(signals);

    const perfRef = doc(firestore, `World/${userId}/signalPerformance/${segKey}`);
    const snap = await getDoc(perfRef);
    if (!snap.exists()) return null;

    const data = snap.data();
    return {
      segmentKey: segKey,
      segment: data.segment || null,
      conversionRate: data.conversionRate || 0,
      negativeOutcomes: data.negativeOutcomes || 0,
      avgTouchesToConvert: data.avgTouchesToConvert,
      totalContacts: data.totalContacts || 0,
      bestSequence: data.winningSequences
        ? Object.entries(data.winningSequences).sort((a, b) => b[1] - a[1])[0]?.[0]
        : null,
      replySubtypes: data.replySubtypes || {},
      confidence: data.totalContacts >= 10 ? 'high' : data.totalContacts >= 3 ? 'medium' : 'low',
    };
  } catch (e) {
    return null;
  }
};

// ──────────────────────────────────────────────
// Update lead context (industry, size) – e.g. when user sets them in CRM
// ──────────────────────────────────────────────

export const updateLeadContext = async (userId, contactId, context = {}) => {
  try {
    if (!userId || !contactId) return;

    const anonId = await hashContactId(userId, contactId);
    const docRef = doc(firestore, `World/${userId}/salesAnalytics/${anonId}`);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return;

    const cur = snap.data();
    const updates = { updatedAt: Date.now() };

    if (context.industry != null) updates.industry = validateIndustry(context.industry);
    if (context.size != null) updates.size = validateSize(context.size);
    if (context.stage != null) updates.stage = context.stage;
    if (context.pacing != null) updates.pacing = context.pacing;

    await setDoc(docRef, { ...cur, ...updates });
  } catch (e) {
    console.warn('[SalesAnalytics] context error (non-critical):', e.message);
  }
};

// ──────────────────────────────────────────────
// Enum validation (enforce standard categories)
// ──────────────────────────────────────────────

const VALID_INDUSTRIES = new Set(_INDUSTRIES_ARR);

const VALID_SIZES = new Set([
  'solo', '2-10', '11-50', '51-200', '201-1000', '1000+',
]);

const VALID_REPLY_TYPES = new Set(['positive', 'objection', 'neutral']);

function validateIndustry(v) { return v && VALID_INDUSTRIES.has(v) ? v : null; }
function validateSize(v) { return v && VALID_SIZES.has(v) ? v : null; }
function validateReplyType(v) { return v && VALID_REPLY_TYPES.has(v) ? v : 'neutral'; }

const SIZE_PROXY_MAP = { SOLO: 'solo', SMALL: '2-10', MID: '11-50', ENT: '201-1000' };
function mapSizeProxyToAnalytics(proxy) { return SIZE_PROXY_MAP[proxy] || null; }

// ──────────────────────────────────────────────
// Completeness score (data quality gate)
// ──────────────────────────────────────────────

export function completenessScore(profile) {
  if (!profile) return 0;
  let score = 0;
  if (profile.channelSeq && profile.totalTouches >= 2) score += 20;
  if (profile.industry) score += 20;
  if (profile.size) score += 20;
  if (profile.replyCount > 0) score += 20;
  if (profile.meeting || profile.deal) score += 20;
  return score;
}

// ──────────────────────────────────────────────
// Ebene 1: Global pattern publish
// ──────────────────────────────────────────────

const GLOBAL_SCHEMA_VERSION = 1;
const MIN_TOUCHES_FOR_GLOBAL = 2;
const MIN_COMPLETENESS_FOR_GLOBAL = 40;

const _publishGlobalPattern = httpsCallable(functions, 'publishGlobalPattern');

/**
 * Publishes a stripped, fully anonymous journey to the global patterns collection.
 * Routes through Cloud Function (Admin SDK) — client has no direct write access.
 */
async function publishToGlobal(profile) {
  try {
    if (!profile) return;

    const score = completenessScore(profile);
    if (score < MIN_COMPLETENESS_FOR_GLOBAL) return;
    if ((profile.totalTouches || 0) < MIN_TOUCHES_FOR_GLOBAL) return;

    await _publishGlobalPattern({
      channelSeq: profile.channelSeq || null,
      daySeq: profile.daySeq || null,
      totalTouches: profile.totalTouches || 0,
      byChannel: profile.byChannel || {},
      industry: profile.industry || null,
      size: profile.size || null,
      replyCount: profile.replyCount || 0,
      avgDaysToReply: profile.avgDaysToReply ?? null,
      replyTypes: profile.replyTypes || {},
      bestScore: profile.bestScore ?? null,
      meeting: profile.meeting || false,
      deal: profile.deal || false,
      dealValue: profile.dealValue ?? null,
    });
  } catch (e) {
    console.warn('[SalesAnalytics] global publish error (non-critical):', e.message);
  }
}

// ──────────────────────────────────────────────
// Shared option lists (used by FlowBuilder + Swipe)
// ──────────────────────────────────────────────

export const INDUSTRY_OPTIONS = [..._INDUSTRIES_ARR];

export const SIZE_OPTIONS = [
  { value: 'solo', label: 'Solo (1)' },
  { value: '2-10', label: 'Micro (2–10)' },
  { value: '11-50', label: 'Small (11–50)' },
  { value: '51-200', label: 'Medium (51–200)' },
  { value: '201-1000', label: 'Large (201–1000)' },
  { value: '1000+', label: 'Enterprise (1000+)' },
];

// ──────────────────────────────────────────────
// Score-to-Outcome Coupling (connects pre-send scores to actual results)
// ──────────────────────────────────────────────

/**
 * Links a pre-send score to the analytics profile so we can later measure
 * whether high-scoring emails actually converted (closed loop).
 *
 * Stored as lastScoreData on the anonymous profile — NO email content.
 */
export const logEmailScore = async (userId, contactId, scoreData = {}) => {
  try {
    if (!userId || !contactId) return;
    const anonId = await hashContactId(userId, contactId);
    const docRef = doc(firestore, `World/${userId}/salesAnalytics/${anonId}`);
    const snap = await getDoc(docRef);
    const cur = snap.exists() ? snap.data() : {};

    const scores = cur.emailScores || [];
    scores.push({
      cs: scoreData.compositeScore ?? null,
      bv: scoreData.buyerValue ?? null,
      dr: scoreData.deliverabilityRisk ?? null,
      rec: scoreData.recommendation ?? null,
      ch: scoreData.contentHash ?? null,
      ts: Date.now(),
    });
    // Keep only last 20 scores per lead to stay lean
    if (scores.length > 20) scores.splice(0, scores.length - 20);

    await setDoc(docRef, {
      ...cur,
      emailScores: scores,
      lastScoreTs: Date.now(),
    }, { merge: true });
  } catch (e) {
    console.warn('[SalesAnalytics] score coupling error (non-critical):', e.message);
  }
};

// ──────────────────────────────────────────────
// Decision Audit Trail (AI Act compliance)
// ──────────────────────────────────────────────

/**
 * Logs an AI decision event for audit / explainability.
 * Used for: lead prioritization, email scoring, auto-send decisions.
 *
 * Stored in: World/{userId}/decisionAudit/{autoId}
 * No PII — only anonymous IDs, decision type, reasoning, and metadata.
 */
export const logDecisionAudit = async (userId, decisionData = {}) => {
  try {
    if (!userId) return;
    const auditRef = collection(firestore, `World/${userId}/decisionAudit`);
    await addDoc(auditRef, {
      type: decisionData.type || 'unknown',
      decision: decisionData.decision || null,
      reasoning: decisionData.reasoning || null,
      humanOverride: decisionData.humanOverride || false,
      overrideReason: decisionData.overrideReason || null,
      metadata: decisionData.metadata || {},
      ts: Date.now(),
    });
  } catch (e) {
    console.warn('[SalesAnalytics] audit trail error (non-critical):', e.message);
  }
};

// ──────────────────────────────────────────────
// DSAR Utilities (Data Subject Access Request — GDPR Art. 15, 17)
// ──────────────────────────────────────────────

/**
 * Sets globalOptOut on a contact and optionally adds their email to the blacklist.
 * This is the canonical way to opt-out a contact across ALL channels.
 *
 * @param {string} userId
 * @param {string} contactId
 * @param {string} source - e.g. 'manual', 'smart_inbox', 'negative_reply', 'unsubscribe'
 * @param {string|null} emailAddress - if provided, also adds to blacklist
 */
export const setGlobalOptOut = async (userId, contactId, source = 'manual', emailAddress = null) => {
  try {
    if (!userId || !contactId) return { success: false };

    const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);
    await setDoc(contactRef, {
      globalOptOut: true,
      globalOptOutAt: Date.now(),
      globalOptOutSource: source,
    }, { merge: true });

    // Also add to email blacklist if address is known
    if (emailAddress) {
      try {
        const blRef = doc(firestore, `World/${userId}/blacklist/${emailAddress.trim().toLowerCase()}`);
        await setDoc(blRef, {
          email: emailAddress.trim().toLowerCase(),
          addedAt: Date.now(),
          source: `opt_out_${source}`,
          contactId,
        }, { merge: true });
      } catch (_) { /* non-critical */ }
    }

    // Audit log
    try {
      await logDecisionAudit(userId, {
        type: 'global_opt_out',
        decision: 'contact_opted_out',
        reasoning: `Source: ${source}`,
        metadata: { contactId, emailAddress: emailAddress ? '[redacted]' : null },
      });
    } catch (_) {}

    return { success: true };
  } catch (e) {
    console.warn('[OptOut] setGlobalOptOut failed:', e.message);
    return { success: false, error: e.message };
  }
};

/**
 * Removes globalOptOut from a contact and optionally removes from blacklist.
 */
export const removeGlobalOptOut = async (userId, contactId, emailAddress = null) => {
  try {
    if (!userId || !contactId) return { success: false };

    const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);
    await setDoc(contactRef, {
      globalOptOut: false,
      globalOptOutRemovedAt: Date.now(),
    }, { merge: true });

    if (emailAddress) {
      try {
        const blRef = doc(firestore, `World/${userId}/blacklist/${emailAddress.trim().toLowerCase()}`);
        await deleteDoc(blRef);
      } catch (_) {}
    }

    return { success: true };
  } catch (e) {
    console.warn('[OptOut] removeGlobalOptOut failed:', e.message);
    return { success: false, error: e.message };
  }
};

/**
 * Lookup: Given a contactId, returns all anonymized analytics data for that person.
 * Used when responding to a DSAR (right of access).
 */
export const dsarLookup = async (userId, contactId) => {
  if (!userId || !contactId) return null;
  try {
    const lookupRef = doc(firestore, `World/${userId}/dsarLookup/${contactId}`);
    const lookupSnap = await getDoc(lookupRef);
    if (!lookupSnap.exists()) return { found: false, contactId };

    const { anonId } = lookupSnap.data();
    const analyticsRef = doc(firestore, `World/${userId}/salesAnalytics/${anonId}`);
    const analyticsSnap = await getDoc(analyticsRef);

    return {
      found: true,
      contactId,
      anonId,
      analyticsData: analyticsSnap.exists() ? analyticsSnap.data() : null,
    };
  } catch (e) {
    console.error('[DSAR] Lookup failed:', e.message);
    return { found: false, error: e.message };
  }
};

/**
 * Erasure: Deletes all anonymized analytics data AND the DSAR lookup entry.
 * Used when responding to a GDPR Art. 17 deletion request (right to erasure).
 */
export const dsarDelete = async (userId, contactId) => {
  if (!userId || !contactId) return { success: false, error: 'Missing userId or contactId' };
  try {
    const lookupRef = doc(firestore, `World/${userId}/dsarLookup/${contactId}`);
    const lookupSnap = await getDoc(lookupRef);

    let analyticsDeleted = false;
    let anonId = null;

    if (lookupSnap.exists()) {
      anonId = lookupSnap.data().anonId;
      const analyticsRef = doc(firestore, `World/${userId}/salesAnalytics/${anonId}`);
      await deleteDoc(analyticsRef);
      analyticsDeleted = true;
    }

    await deleteDoc(lookupRef);

    // Clear from in-memory caches
    const cacheKey = `${userId}:${contactId}`;
    hashCache.delete(cacheKey);
    dsarWritten.delete(cacheKey);

    // Log the erasure for audit
    try {
      await logDecisionAudit(userId, {
        type: 'dsar_erasure',
        decision: 'data_deleted',
        reasoning: 'GDPR Art. 17 right to erasure',
        metadata: { contactId, anonId, analyticsDeleted },
      });
    } catch (_) {}

    return { success: true, contactId, anonId, analyticsDeleted };
  } catch (e) {
    console.error('[DSAR] Delete failed:', e.message);
    return { success: false, error: e.message };
  }
};

// Conditions that automatically log analytics outcomes when answered
export const ANALYTICS_CONDITIONS = {
  meeting_booked: { outcomeKey: 'meeting', outcomeValue: true },
  deal_closed: { outcomeKey: 'deal', outcomeValue: true },
  responded_positively: { replyAnalytics: true },
};

export default {
  logSalesAnalytics,
  logReplyAnalytics,
  logOutcomeAnalytics,
  logEmailScore,
  logDecisionAudit,
  setGlobalOptOut,
  removeGlobalOptOut,
  dsarLookup,
  dsarDelete,
  updateLeadContext,
  getSegmentPerformance,
  buildSegmentKey,
  classifyReplyType,
  validateReplySubtype,
  completenessScore,
  mapToChannel,
  REPLY_SUBTYPES,
  INDUSTRY_OPTIONS,
  SIZE_OPTIONS,
  ANALYTICS_CONDITIONS,
};
